import { useEffect } from 'react';
import { type SelectedNode, useGallerySelection } from '@/context/GallerySelectionContext';

/**
 * Keeps a selected child folder/album in step with the children actually being rendered.
 *
 * `SelectedNode` is a snapshot copied at click time, and nothing else reconciles it:
 * `useReconcileSelectionWithPage` deliberately skips the nodes arm, because a child sits on no
 * asset page and paging must not drop it. That left two stale states with no owner — rename the
 * selected child through its own context menu and the selection bar's label, the inspector header
 * and `operationTarget.name` (which becomes the DownloadZip filename) all kept the old name; trash
 * it and the bar went on offering actions for a node that no longer existed.
 *
 * Mounted above ChildrenSection rather than inside it: the section returns null when a folder has
 * no children, so trashing the last remaining child would unmount the very thing responsible for
 * noticing.
 */
export const useReconcileChildSelection = (children: Array<SelectedNode> | undefined) => {
  const { selection, setSelectedNode } = useGallerySelection();

  useEffect(() => {
    if (selection.kind !== 'nodes' || children === undefined) {
      return;
    }

    const selected = selection.item;
    const live = children.find((child) => child.id === selected.id);

    if (!live) {
      setSelectedNode(undefined);
      return;
    }

    // Compared field by field rather than by identity: `children` is rebuilt on every render, so an
    // identity check would write on every pass and loop.
    if (live.name !== selected.name || live.slug !== selected.slug || live.type !== selected.type) {
      setSelectedNode(live);
    }
  }, [children, selection, setSelectedNode]);
};
